/*
 * -----------------------------------------------------------------------
 *  <copyright file="actions-menu/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { all } from '@awesome.me/kit-2cbcf7d4a0/icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Grow from '@mui/material/Grow';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IActionsMenuProps from './actions-menu.interface';
import { useTranslation } from 'hooks';
import './styles.scss';
import { textToId } from '@utils/automation-test-helpers';

library.add({ ...all });

const ActionsMenu = ({ actions, transformOrigin, anchorOrigin, height, customClassName }: IActionsMenuProps) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <div className={`menu-wrapper ${anchorEl ? 'opened' : ''}`}>
            <Button
                aria-controls="actions-menu"
                aria-haspopup="false"
                aria-describedby="actions-menu"
                className="btn-dots"
                style={{ height: height || 42 }}
                onClick={handleClick}
                data-testid="actions-menu"
            >
                <FontAwesomeIcon icon={['fas', 'ellipsis-v']} className="ellipsis-icon" />
            </Button>
            <Menu
                id="actions-menu"
                className={`menu-items ${customClassName || ''}`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slots={{ transition: Grow }}
                anchorOrigin={{ vertical: anchorOrigin.vertical, horizontal: anchorOrigin.horizontal }}
                transformOrigin={{ vertical: transformOrigin.vertical, horizontal: transformOrigin.horizontal }}
                slotProps={{
                    root: {
                        slotProps: {
                            backdrop: {
                                style: {
                                    backgroundColor: 'transparent',
                                },
                            },
                        },
                    },
                }}
            >
                {actions &&
                    actions.map((item, index) => (
                        <MenuItem
                            className={`menu-item ${!item.enabled ? 'disabled' : ''}`}
                            key={item.label}
                            onClick={() => {
                                handleClose();
                                setTimeout(() => {
                                    if (item.enabled) {
                                        item.onClick(item);
                                    }
                                }, 0);
                            }}
                            data-testid={`actions-menu-item-${textToId(item.label, index)}`}
                        >
                            {item.icon && <FontAwesomeIcon icon={item.icon} className="menu-icon" />}
                            <Typography variant="inherit" className="label-menu">
                                {t(item.label)}
                            </Typography>
                        </MenuItem>
                    ))}
            </Menu>
        </div>
    );
};

export default ActionsMenu;
